<template>
  <div class="onboarding-connect-extensions">
    <!-- Left selection : steps -->
    <OnboardingSteps
      :current-step="'extensions'"
      :current-network="currentNetwork"
      @network-selected="handleNetworkSelected"
    />

    <!-- Right section : extension connection -->
    <div class="onboarding-content">
      <!-- Title section -->
      <div class="onboarding-header">
        <img
          :src="currentNetworkIcon"
          :alt="currentNetworkName"
          class="onboarding-network-icon"
        />
        <div class="onboarding-header-content">
          <h1 class="onboarding-header-title">
            {{ $t('onboarding.connect_extensions.connection_title', { networkName: currentNetworkName }) }}
            <svg class="native-icon" width="26" height="26" viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" stroke-width="3" stroke="none" fill="#111827"><path d="M37.34,7.36a.12.12,0,0,1,.18.13c-.47,1.86-2.78,12.63,5.57,19.62,8.16,6.84,8.41,17.13,2.33,24-7.27,8.23-19.84,6.78-25.25,1.37C16.36,48.69,9.44,36.33,21.29,26a.1.1,0,0,1,.16,0c.29,1.23,2.3,9,7.66,10,.25,0,.37-.11.25-.34C27.78,32.6,20.66,17,37.34,7.36Z" stroke-linecap="round"/></svg>
          </h1>
          <p class="onboarding-header-subtitle">
            {{ $t('onboarding.connect_extensions.connection_subtitle', { networkName: currentNetworkName }) }}
          </p>
        </div>
      </div>

      <!-- Features badges that require extension -->
      <div class="onboarding-features-badges" v-if="networkFeatures.length > 0">
        <span
          class="onboarding-feature-badge"
          v-for="feature in networkFeatures"
          :key="feature"
          :data-tooltip="$t(`onboarding.features.${feature}.description`)"
        >
          {{ $t(`onboarding.features.${feature}.title`) }}
        </span>
      </div>

      <!-- Extension installation guide (hidden after success) -->
      <div v-if="!extensionSuccess" class="onboarding-extension-guide">
        <!-- Step 1: Install extension (hidden if extension is already installed) -->
        <div class="onboarding-extension-step" v-if="extensionInstalled !== true">
          <div class="onboarding-step-number">1</div>
          <div class="onboarding-step-content">
            <h3 class="onboarding-step-title">{{ $t('onboarding.connect_extensions.step1_title') }}</h3>
            <p class="onboarding-step-description">
              {{ $t('onboarding.connect_extensions.step1_description') }}
            </p>
            <a 
              :href="extensionUrl" 
              target="_blank"
              class="onboarding-btn-extension"
            >
              <img src="../assets/icons/extension.svg" alt="Extension" class="onboarding-btn-icon" />
              {{ $t('onboarding.connect_extensions.download_extension') }}
            </a>
            <!-- GIF tutorial toggle -->
            <button class="onboarding-gif-toggle" @click="gifStep1Open = !gifStep1Open">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path v-if="!gifStep1Open" d="M5 2.5L9.5 7L5 11.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                <path v-else d="M2.5 5L7 9.5L11.5 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              {{ gifStep1Open ? $t('onboarding.connect_extensions.tutorial_hide') : $t('onboarding.connect_extensions.tutorial_show') }}
            </button>
            <div v-if="gifStep1Open" class="onboarding-gif-container" @click="openLightbox(extensionGif)">
              <img :src="extensionGif" alt="Tutorial extension" class="onboarding-gif" />
              <div class="onboarding-gif-expand-hint">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10 2H14V6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M6 14H2V10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M14 2L9 7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M2 14L7 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </div>
            </div>
          </div>
        </div>

        <!-- Step 2: Connect on the network -->
        <div class="onboarding-extension-step">
          <!-- Step number only shown if step 1 is also visible (extension not installed) -->
          <div class="onboarding-step-number" v-if="extensionInstalled !== true">2</div>
          <div class="onboarding-step-content">
            <h3 class="onboarding-step-title">{{ $t('onboarding.connect_extensions.step2_title', { networkName: currentNetworkName }) }}</h3>
            <p class="onboarding-step-description">
              {{ $t('onboarding.connect_extensions.step2_description', { networkName: currentNetworkName }) }}
            </p>
            <button
              @click="openNetworkWithContext"
              class="onboarding-btn-connect"
            >
              <img src="../assets/icons/native-icon.svg" alt="native" class="onboarding-btn-icon2" />
              {{ $t('onboarding.connect_extensions.connect_pages', { networkName: currentNetworkName }) }}
            </button>
            <!-- GIF tutorial toggle (network-specific) -->
            <button class="onboarding-gif-toggle" v-if="networkExtensionGif" @click="gifStep2Open = !gifStep2Open">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path v-if="!gifStep2Open" d="M5 2.5L9.5 7L5 11.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                <path v-else d="M2.5 5L7 9.5L11.5 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              {{ gifStep2Open ? $t('onboarding.connect_extensions.tutorial_hide') : $t('onboarding.connect_extensions.tutorial_show') }}
            </button>
            <div v-if="gifStep2Open && networkExtensionGif" class="onboarding-gif-container" @click="openLightbox(networkExtensionGif)">
              <img :src="networkExtensionGif" :alt="`Tutorial ${currentNetworkName}`" class="onboarding-gif" />
              <div class="onboarding-gif-expand-hint">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10 2H14V6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M6 14H2V10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M14 2L9 7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M2 14L7 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </div>
            </div>
          </div>
        </div>

        <!-- YouTube video embed -->
        <!-- <div class="onboarding-video-container" v-if="tutorialVideoId">
          <iframe
            :src="`https://www.youtube.com/embed/${tutorialVideoId}`"
            frameborder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowfullscreen
            class="onboarding-video"
          ></iframe>
        </div> -->
      </div>

      <!-- Success message after extension connection -->
      <div v-else class="onboarding-success-container">
        <div class="onboarding-success-icon">
          <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
            <circle cx="32" cy="32" r="32" fill="#10B981" fill-opacity="0.1"/>
            <path d="M20 32L28 40L44 24" stroke="#10B981" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <h2 class="onboarding-success-title">{{ $t('onboarding.connect_extensions.success_title') }}</h2>
        <p class="onboarding-success-subtitle">
          <!-- {{ newlyConnectedPages.length > 1 ? $t('onboarding.connect_extensions.success_subtitle_plural', { count: newlyConnectedPages.length, networkName: currentNetworkName }) : $t('onboarding.connect_extensions.success_subtitle_singular', { count: newlyConnectedPages.length, networkName: currentNetworkName }) }} -->
          {{ $t('onboarding.connect_extensions.success_subtitle_generic', { networkName: currentNetworkName }) }}
        </p>
        
        <!-- List of newly connected pages -->
        <div class="onboarding-success-pages">
          <div 
            v-for="page in newlyConnectedPages" 
            :key="page.id"
            class="onboarding-success-page-card"
          >
            <img
              v-if="page.avatar && !imageLoadErrors[page.id]"
              :src="page.avatar"
              :alt="page.name"
              class="onboarding-success-page-avatar"
              @error="handleImageError(page.id)"
            />
            <div
              v-else
              class="onboarding-success-page-avatar avatar-placeholder"
            >
              {{ page.name.charAt(0).toUpperCase() }}
            </div>
            <div class="onboarding-success-page-info">
              <h4 class="onboarding-success-page-name">{{ page.name }}</h4>
              <p class="onboarding-success-page-username">@{{ page.username || page.name }}</p>
            </div>
            <div class="onboarding-success-page-badge">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M13.333 4L5.99967 11.3333L2.66634 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              {{ $t('onboarding.connect_extensions.success_page_badge') }}
            </div>
          </div>
        </div>

        <p class="onboarding-success-footer">
          {{ $t('onboarding.connect_extensions.success_footer') }}
        </p>
      </div>


      <!-- Skip warning modal -->
      <modal-popup class="onboarding-warning-modal" />

      <!-- Footer with navigation -->
      <div class="onboarding-footer-container">
        <div class="onboarding-footer" :class="{ 'centered': extensionSuccess }">
          <button 
            v-if="!extensionSuccess"
            class="onboarding-btn-secondary" 
            @click="skipExtension"
          >
            {{ $t('onboarding.connect_extensions.skip_step') }}
          </button>
          <button
            class="onboarding-btn-primary"
            :disabled="!extensionSuccess"
            @click="goToNextPage"
          >
            {{ nextButtonLabel }}
          </button>
        </div>
      </div>
    </div>

    <!-- GIF lightbox overlay -->
    <transition name="lightbox-fade">
      <div v-if="gifLightboxSrc" class="onboarding-gif-lightbox" @click.self="closeLightbox">
        <button class="onboarding-gif-lightbox-close" @click="closeLightbox">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15 5L5 15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M5 5L15 15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
        <img :src="gifLightboxSrc" alt="Tutorial" class="onboarding-gif-lightbox-img" />
      </div>
    </transition>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { EventBus } from '@/event-bus';
import Notif from '../../mixins/Notif.vue';
import Pages from '../../mixins/Pages.vue';
import OnboardingSteps from '../shared/OnboardingSteps.vue';
import modalPopup from '@/components/dash/modalPopup.vue';
import { persistOnboardingPages, restoreOnboardingPages } from '../utils/onboardingPersistence';
import { getNetworkName, getNetworkIcon } from '../utils/networkMaps';

const EXTENSION_ID = 'lmojmckfefcipoompcaedhnbbkeceohm'
const EXTENSION_IDS = [
  'lmojmckfefcipoompcaedhnbbkeceohm', // production
  'bpefhalcahkfhbloencicallkkhieclm'  // dev / sandbox
]

export default {
  name: 'StepConnectExtensions',

  components: {
    OnboardingSteps,
    modalPopup
  },

  mixins: [Notif, Pages],

  data() {
    return {
      currentNetworkIndex: 0,
      // currentPageIndex: 0,
      imageLoadErrors: {},
      // lastExtensionConnectedBatch: [], // IDs des pages connectées ensemble par l'extension (même compte)
      extensionSuccess: false, // True after successful extension connection
      newlyConnectedPages: [], // List of pages just connected via extension
      extensionInstalled: null, // null = detecting, true = installed, false = not installed
      gifStep1Open: true, // GIF tutorial visibility for step 1 (open by default if 2 steps)
      gifStep2Open: false,  // GIF tutorial visibility for step 2 (closed if 2 steps, open if 1 step)
      gifLightboxSrc: null  // src of the GIF currently displayed fullscreen (null = closed)
    }
  },

  watch: {
    // Automatically open the appropriate GIF based on whether extension is installed (1 or 2 steps)
    extensionInstalled: {
      immediate: true,
      handler(val) {
        if (val === true) {
          // Only step 2 is visible: open step 2 GIF
          this.gifStep1Open = false
          this.gifStep2Open = true
        } else {
          // Both steps are visible (not installed / detecting): open step 1, close step 2
          this.gifStep1Open = true
          this.gifStep2Open = false
        }
      }
    },

    // Persist current network index whenever it changes so page reloads return to the right network
    currentNetworkIndex(val) {
      localStorage.setItem('onboarding_current_index', val.toString())
    },

    // Restore the saved network index once the parent (Onboarding.vue) has re-populated the Vuex
    // store after a page reload. The parent mounts after the child, so selectedNetworks is empty
    // when this component's mounted() runs — this watcher catches the first non-empty update.
    selectedNetworks(networks) {
      if (networks.length === 0 || this._networkIndexRestored) return
      this._networkIndexRestored = true
      const savedIndex = localStorage.getItem('onboarding_current_index')
      if (savedIndex === null) return
      const parsed = Number.parseInt(savedIndex)
      if (!Number.isNaN(parsed) && parsed > 0 && parsed < networks.length) {
        console.log('[StepConnectExtensions] selectedNetworks populated → restoring index', parsed, '→', networks[parsed])
        this.currentNetworkIndex = parsed
      }
    }
  },

  computed: {
    ...mapState('onboarding', [
      'selectedNetworks',
      'selectedFeatures',
      'connectedPages',
      'selectedPageIds'
    ]),

    currentNetwork() {
      return this.selectedNetworks[this.currentNetworkIndex] || null
    },

    currentNetworkName() {
      return getNetworkName(this.currentNetwork)
    },

    currentNetworkIcon() {
      return getNetworkIcon(this.currentNetwork)
    },

    // Label for the next button
    nextButtonLabel() {
      // Before connection: button is disabled
      if (!this.extensionSuccess) {
        return this.$t('onboarding.connect_extensions.next_button_waiting')
      }
      
      // After successful connection: return to StepConnectPages
      return this.$t('onboarding.connect_extensions.back_to_pages', { networkName: this.currentNetworkName })
    },

    // Get features that require extension for this network
    networkFeatures() {
      if (!this.currentNetwork) return []
      
      // Check if user selected native connection for this network
      const hasNative = this.selectedFeatures.some(f => 
        f === `${this.currentNetwork}.native` || f === 'twitter.native' || f === 'x.native'
      )
      
      if (!hasNative) return []
      
      // Return all feature labels for this network to display as badges
      const featureLabels = {
        facebook: [
          'facebook.native_post',
          'facebook.groups',
          'facebook.personal',
        ],
        instagram: [
          'instagram.native_post',
          'instagram.stories',
          'instagram.mention',
          'instagram.music',
          'instagram.hashtag',
          'instagram.sticker'
        ],
        linkedin: [
          'linkedin.native_post',
          'linkedin.mentions',
          'linkedin.groups',
          'linkedin.carousel',
          'linkedin.poll',
          'linkedin.inbox'
        ],
        tiktok: [
          'tiktok.mentions',
          'tiktok.reporting',
          'tiktok.music',
          'tiktok.video',
        ],
        twitter: [
          'twitter.native_post',
          'twitter.mentions'
        ],
        x: [
          'twitter.native_post',
          'twitter.mentions'
        ]
      }
      
      return featureLabels[this.currentNetwork] || []
    },

    // Chrome extension URL
    extensionUrl() {
      return `https://chrome.google.com/webstore/detail/mashup-web/${EXTENSION_ID}`
    },

    // GIF tutorial for step 1 (always extension.gif)
    extensionGif() {
      return require('@/assets/gifs/extension.gif')
    },

    // GIF tutorial for step 2 (network-specific)
    networkExtensionGif() {
      const facebookGif = require('@/assets/gifs/facebook_extension.gif')
      const gifs = {
        facebook: facebookGif,
        tiktok: require('@/assets/gifs/tiktok_extension.gif'),
        linkedin: require('@/assets/gifs/linkedin_extension.gif'),
        twitter: facebookGif,
        x: facebookGif
      }
      return gifs[this.currentNetwork] || null
    },

    // Tutorial video ID based on network
    tutorialVideoId() {
      const videos = {
        linkedin: 'YOUR_LINKEDIN_VIDEO_ID',
        tiktok: 'YOUR_TIKTOK_VIDEO_ID',
        facebook: 'YOUR_FACEBOOK_VIDEO_ID'
      }
      return videos[this.currentNetwork] || null
    }
  },

  methods: {
    goToNextPage() {
      console.log('[StepConnectExtensions] goToNextPage - moving to next network')
      this.goToNextNetwork()
    },

    // Detect whether the Mashup Web Social extension is installed in the browser.
    // Method 1: check for window property injected by the extension content script.
    // Method 2: try to load a known resource from the extension package
    //   (requires the icon to be declared in web_accessible_resources).
    checkExtensionInstalled() {
      if (window.__mashupWebExtension === true) {
        console.log('[StepConnectExtensions] Extension detected via window flag')
        this.extensionInstalled = true
        return
      }

      const tryLoad = (id) => new Promise((resolve) => {
        const img = new Image()
        const timer = setTimeout(() => resolve(false), 1500)
        img.onload = () => { clearTimeout(timer); resolve(true) }
        img.onerror = () => { clearTimeout(timer); resolve(false) }
        img.src = `chrome-extension://${id}/icons/icon_16.png`
      })

      console.log('[StepConnectExtensions] Checking extension presence for IDs:', EXTENSION_IDS)
      Promise.all(EXTENSION_IDS.map(tryLoad)).then(results => {
        const found = results.some(Boolean)
        console.log('[StepConnectExtensions] Extension detection results:', results, '→', found ? 'installed' : 'not installed')
        this.extensionInstalled = found
      })
    },

    persistOnboardingStateBeforeNavigation() {
      persistOnboardingPages(this.$store)
      console.log('[StepConnectExtensions] Onboarding state persisted to localStorage')
    },

    goToNextNetwork() {
      console.log('[StepConnectExtensions] Returning to StepConnectPages for network:', this.currentNetwork)
      this.persistOnboardingStateBeforeNavigation()
      
      this.$router.push({ 
        name: 'onboarding-connectPages',
        params: { 
          fromExtensions: true,
          networkIndex: this.currentNetworkIndex
        }
      })
    },

    skipExtension() {
      this.showSkipWarning()
    },

    showSkipWarning() {
      console.log('[StepConnectExtensions] Opening skip warning modal')
      
      // Build features list HTML
      const featuresList = this.networkFeatures
        .map(f => `<li>${this.$t(`onboarding.features.${f}.title`)}</li>`)
        .join('')
      
      const subtitle = `
        <p style="margin-bottom: 16px;">${this.$t('onboarding.connect_extensions.skip_warning_intro', { networkName: this.currentNetworkName })}</p>
        <ul style="background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 8px; padding: 16px 20px; margin: 16px 0; list-style: none;">
          ${featuresList}
        </ul>
        <p style="font-size: 13px; color: #6b7280; font-style: italic; margin-top: 16px;">${this.$t('onboarding.connect_extensions.skip_warning_footer')}</p>
      `
      
      EventBus.$emit('showModal',
        this.$t('onboarding.modale.extension.exit.title') || 'Ignorer l\'installation de l\'extension ?',
        subtitle,
        this.$t('onboarding.modale.exit.confirm') || 'Ignorer',
        this.$t('onboarding.modale.exit.cancel') || 'Annuler',
        this.confirmSkip,
        null
      )
    },

    confirmSkip() {
      console.log('[StepConnectExtensions] Skip confirmed by user, returning to StepConnectPages')
      this.persistOnboardingStateBeforeNavigation()
      this.$router.push({ 
        name: 'onboarding-connectPages',
        params: { 
          fromExtensions: true,
          networkIndex: this.currentNetworkIndex
        }
      })
    },

    openNetworkWithContext() {
      console.log('[StepConnectExtensions] Opening network with onboarding context')
      
      // CRITICAL: Save entire Vuex state to localStorage to survive hard reload
      // Extension will redirect back causing a full page refresh
      localStorage.setItem('onboarding_mode', 'true')
      localStorage.setItem('onboarding_current_network', this.currentNetwork)
      localStorage.setItem('onboarding_current_index', this.currentNetworkIndex.toString())
      localStorage.setItem('onboarding_selected_networks', JSON.stringify(this.selectedNetworks))
      localStorage.setItem('onboarding_selected_features', JSON.stringify(this.selectedFeatures))
      persistOnboardingPages(this.$store)
      
      // CRITICAL: Save network_ids of pages BEFORE opening extension
      // This allows us to detect NEW pages when extension returns
      const pagesBefore = this.connectedPages[this.currentNetwork] || []
      const networkIdsBefore = pagesBefore.map(p => p.network_id)
      localStorage.setItem('onboarding_pages_before_extension', JSON.stringify(networkIdsBefore))
      console.log('[StepConnectExtensions] Vuex state saved to localStorage')
      console.log('[StepConnectExtensions] Pages before extension:', networkIdsBefore)
      
      // Store onboarding context in localStorage for extension to read
      const context = {
        active: true,
        network: this.currentNetwork,
        timestamp: Date.now()
      }
      
      localStorage.setItem('mashup_onboarding_context', JSON.stringify(context))
      console.log('[StepConnectExtensions] Context stored in localStorage:', context)
      
      // Build network URL with query parameters
      const networkUrls = {
        linkedin: 'https://www.linkedin.com',
        tiktok: 'https://www.tiktok.com',
        facebook: 'https://www.facebook.com',
        twitter: 'https://x.com',
        x: 'https://x.com'
      }
      
      const baseUrl = networkUrls[this.currentNetwork] || networkUrls.linkedin
      const url = `${baseUrl}/?mashup_onboarding=true&network=${this.currentNetwork}`
      
      console.log('[StepConnectExtensions] Opening:', url)
      
      // Open network in new tab
      window.open(url, '_blank')
      
      // Show info notification
      this.notify({
        time: 5000,
        title: this.$t('onboarding.notifications.info_title'),
        text: this.$t('onboarding.connect_extensions.info_use_extension', { networkName: this.currentNetworkName }),
        color: 'primary'
      })
    },

    // LEGACY METHOD - NO LONGER USED (kept for reference)
    // Extension return is now handled directly in mounted() via mashup_extension_return
    // handleExtensionReturn() {
    //   console.log('[StepConnectExtensions] Retour depuis l\'extension')
    //   // This method is deprecated - extension return logic is in mounted()
    // },

    handleNetworkSelected(network) {
      const index = this.selectedNetworks.indexOf(network)
      if (index !== -1) {
        this.currentNetworkIndex = index
        // Reset success state when switching networks
        this.extensionSuccess = false
        this.newlyConnectedPages = []
        // Set GIF states based on extension installation
        if (this.extensionInstalled === true) {
          this.gifStep1Open = false
          this.gifStep2Open = true
        } else {
          this.gifStep1Open = true
          this.gifStep2Open = false
        }
      }
    },

    handleImageError(id) {
      this.imageLoadErrors = { ...this.imageLoadErrors, [id]: true }
    },

    openLightbox(src) {
      this.gifLightboxSrc = src
    },

    closeLightbox() {
      this.gifLightboxSrc = null
    }
  },

  mounted() {
    console.log('=== [StepConnectExtensions] MOUNTED ===')
    this.checkExtensionInstalled()
    this._lightboxKeyHandler = (e) => { if (e.key === 'Escape') this.closeLightbox() }
    document.addEventListener('keydown', this._lightboxKeyHandler)
    
    // CRITICAL: Restore Vuex state from localStorage if coming back from extension
    // Extension redirect causes hard reload, losing all Vuex state
    const extensionReturnStr = localStorage.getItem('mashup_extension_return')
    const isReturningFromExtension = !!extensionReturnStr
    
    if (isReturningFromExtension) {
      console.log('[StepConnectExtensions] Returning from extension - restoring state from localStorage')
      
      // Restore all Vuex state
      const savedNetworks = localStorage.getItem('onboarding_selected_networks')
      const savedFeatures = localStorage.getItem('onboarding_selected_features')
      const savedNetworkIndex = localStorage.getItem('onboarding_current_index')
      
      if (savedNetworks) {
        this.$store.commit('onboarding/SET_NETWORKS', JSON.parse(savedNetworks))
      }
      if (savedFeatures) {
        this.$store.commit('onboarding/SET_FEATURES', JSON.parse(savedFeatures))
      }
      
      // Restore page connection data (connectedPages, selectedPageIds, preExistingPageIds, etc.)
      restoreOnboardingPages(this.$store)
      
      if (savedNetworkIndex) {
        this.currentNetworkIndex = parseInt(savedNetworkIndex)
      }
      
      console.log('[StepConnectExtensions] State restored:', {
        networks: this.selectedNetworks,
        currentNetwork: this.currentNetwork,
        currentNetworkIndex: this.currentNetworkIndex
      })
    }
    
    console.log('[StepConnectExtensions] Current network:', this.currentNetwork)
    
    // Check if returning from extension via localStorage (written by content script)
    // const extensionReturnStr = localStorage.getItem('mashup_extension_return')
    
    if (extensionReturnStr) {
      try {
        const returnData = JSON.parse(extensionReturnStr)
        console.log('[StepConnectExtensions] Retour depuis extension détecté (localStorage)', returnData)
        
        // Verify that the data is recent (less than 30 seconds old)
        const age = Date.now() - returnData.timestamp
        if (age < 30000 && returnData.success) {
          console.log('[StepConnectExtensions] Données valides, traitement du succès')
          
          // CRITICAL: Read network_ids BEFORE cleaning localStorage
          const networkIdsBeforeStr = localStorage.getItem('onboarding_pages_before_extension')
          const networkIdsBefore = networkIdsBeforeStr ? JSON.parse(networkIdsBeforeStr) : []
          
          console.log('[StepConnectExtensions] Pages before extension (from localStorage):', networkIdsBefore)
          
          // Clean localStorage AFTER reading the data
          localStorage.removeItem('mashup_extension_return')
          localStorage.removeItem('mashup_onboarding_context')
          localStorage.removeItem('onboarding_pages_before_extension')
          
          // Restore network (handling twitter/x aliases)
          const targetNetwork = returnData.network
          const networkAlias = (targetNetwork === 'twitter') ? 'x' : (targetNetwork === 'x' ? 'twitter' : targetNetwork)
          
          let networkIndex = this.selectedNetworks.indexOf(targetNetwork)
          if (networkIndex === -1) {
            networkIndex = this.selectedNetworks.indexOf(networkAlias)
          }
          if (networkIndex !== -1) {
            this.currentNetworkIndex = networkIndex
          }
          
          // Fetch updated pages from backend
          this.getPages().then((response) => {
            if (response.ok && response.body && response.body.pages) {
              const allPagesAfter = response.body.pages
              console.log('[StepConnectExtensions] All pages from backend:', allPagesAfter.length)
              console.log('[StepConnectExtensions] Looking for network:', targetNetwork, 'or alias:', networkAlias)
              console.log('[StepConnectExtensions] Available networks:', [...new Set(allPagesAfter.map(p => p.network))])
              
              const networkPagesAfter = allPagesAfter.filter(p => p.network === targetNetwork || p.network === networkAlias)
              
              console.log('[StepConnectExtensions] Pages after extension:', networkPagesAfter.map(p => p.network_id))
              
              // Find NEW pages (those NOT in pagesBefore)
              const newPages = networkPagesAfter.filter(p => !networkIdsBefore.includes(p.network_id))
              
              console.log('[StepConnectExtensions] NEW pages connected by extension:', newPages.map(p => `${p.name} (${p.id})`))
              
              // Add new pages to store and mark them as natively connected
              const newPageIds = []
              newPages.forEach(page => {
                this.$store.commit('onboarding/ADD_CONNECTED_PAGE', {
                  network: returnData.network,
                  page: {
                    id: page.id,
                    network_id: page.network_id,
                    name: page.name,
                    username: page.username || page.name,
                    avatar: page.avatar || null,
                    story_config: page.story_config || null, // CRITICAL: Include story_config for native status
                    hasUuid: true
                  }
                })
                
                // Mark page as natively connected in the store
                // This ensures Summary shows correct native status (green badge)
                this.$store.commit('onboarding/SET_NATIVE_CONNECTION', {
                  network: returnData.network,
                  pageId: page.id,
                  connected: true,
                  storyConfig: page.story_config || null
                })
                
                newPageIds.push(page.id)
              })
              
              // Also mark ALL existing pages of this network as natively connected
              // The extension connects the entire account, not just new pages
              const existingPages = this.connectedPages[returnData.network] || []
              existingPages.forEach(page => {
                this.$store.commit('onboarding/SET_NATIVE_CONNECTION', {
                  network: returnData.network,
                  pageId: page.id,
                  connected: true,
                  storyConfig: page.story_config || null
                })
              })
              
              // CRITICAL: Ensure all newly connected pages are selected
              // (otherwise they would be disconnected in StepSummary finishOnboarding)
              if (newPageIds.length > 0) {
                const currentSelected = this.$store.state.onboarding.selectedPageIds[returnData.network] || []
                const mergedSelected = [...new Set([...currentSelected, ...newPageIds])]
                this.$store.commit('onboarding/SET_SELECTED_PAGE_IDS', {
                  network: returnData.network,
                  pageIds: mergedSelected
                })
                console.log(`[StepConnectExtensions] Auto-selected new pages for ${returnData.network}:`, mergedSelected)
              }
              
              // Set success state and store pages for display
              this.extensionSuccess = true
              
              // Show new pages if any, otherwise show existing pages of this network
              // (LinkedIn/TikTok pages already exist from OAuth — extension only adds native connection)
              if (newPages.length > 0) {
                this.newlyConnectedPages = newPages
              } else {
                this.newlyConnectedPages = (this.connectedPages[returnData.network] || []).map(p => ({
                  ...p,
                  // Ensure backend-compatible fields for the card template
                  avatar: p.avatar || null,
                  username: p.username || p.name
                }))
              }
              
              // CRITICAL: Persist state to localStorage after extension adds new pages
              // Without this, pages added via extension would be lost if user navigates away
              this.persistOnboardingStateBeforeNavigation()
              
              // Show success notification
              const networkMessages = {
                // linkedin: `${newPages.length} page${newPages.length > 1 ? 's' : ''} LinkedIn de ce compte connectée${newPages.length > 1 ? 's' : ''} avec succès`,
                linkedin: 'Vos pages LinkedIn ont été connectées avec succès',
                tiktok: 'Votre compte TikTok a été connecté avec succès',
                facebook: 'Vos pages Facebook ont été connectées avec succès'
              }
              
              this.notify({
                time: 5000,
                title: this.$t('onboarding.connect_extensions.success_connection_title'),
                // text: networkMessages[returnData.network] || 'Pages connectées via l\'extension Chrome',
                text: this.$t('onboarding.connect_extensions.success_connection_message'),
                color: 'success'
              })
              
              // User clicks button manually to continue (no auto-advance)
            }
          }).catch(error => {
            console.error('[StepConnectExtensions] Error fetching pages after extension:', error)
          })
          
          return // Skip the rest of mounted logic
        } else {
          console.log('[StepConnectExtensions] Données trop anciennes ou invalides, nettoyage')
          localStorage.removeItem('mashup_extension_return')
        }
      } catch (error) {
        console.error('[StepConnectExtensions] Erreur parsing extension return:', error)
        localStorage.removeItem('mashup_extension_return')
      }
    }
    
    // Legacy: Detect return from extension via query params (old method)
    if (this.$route.query.success === 'true') {
      this.handleExtensionReturn()
      return
    }
    
    // Get network from route params OR query params (extension redirect uses query)
    const networkFromParams = this.$route.params.network
    const networkFromQuery = this.$route.query.network
    const targetNetwork = networkFromParams || networkFromQuery
    
    if (targetNetwork) {
      const index = this.selectedNetworks.indexOf(targetNetwork)
      if (index !== -1) {
        this.currentNetworkIndex = index
        console.log('[StepConnectExtensions] Set network from route to:', targetNetwork, 'index:', index)
      } else {
        console.warn('[StepConnectExtensions] Network not found in selectedNetworks:', targetNetwork)
      }
    } else if (this.selectedNetworks.length > 0) {
      // Reload case without route info: if selectedNetworks is already populated (unlikely but
      // possible if navigation happened within the SPA without reload), restore index immediately.
      // The common reload path is handled by the selectedNetworks watcher above.
      const savedIndex = localStorage.getItem('onboarding_current_index')
      if (savedIndex !== null) {
        const parsed = Number.parseInt(savedIndex)
        if (!Number.isNaN(parsed) && parsed < this.selectedNetworks.length) {
          this._networkIndexRestored = true
          this.currentNetworkIndex = parsed
          console.log('[StepConnectExtensions] Restored network index immediately (store was populated):', parsed, '→', this.currentNetwork)
        }
      }
    }
  },

  beforeDestroy() {
    document.removeEventListener('keydown', this._lightboxKeyHandler)
  }
}
</script>

<style lang="scss" scoped>
@import '../variables.scss';

.onboarding-connect-extensions {
  display: flex;
  min-height: 100vh;
}

.onboarding-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 64px;
  overflow-y: auto;
  overflow-x: hidden;

  &::after {
    content: '';
    display: block;
    height: 100px;
    flex-shrink: 0;
  }
}

/* Header section - réutilisé de StepConnectNatives */
.onboarding-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 20px;
  margin-bottom: 24px;

  .onboarding-network-icon {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: $bg-ui-selected;
    padding: 12px;
  }

  .onboarding-header-content {
    flex: 1;
  }

  .onboarding-header-title {
    font-size: 28px;
    font-weight: 700;
    color: #111827;
    margin: 0 0 8px 0;

   .native-icon {
      display: inline-block;
      vertical-align: -4px;
      margin-left: -6px;
    }
  }

  .onboarding-header-subtitle {
    font-size: 16px;
    color: #6b7280;
    margin: 0;
    max-width: 50ch;
  }
}

/* Features badges */
.onboarding-features-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 32px;
  justify-content: center;
  overflow: visible;
}

.onboarding-feature-badge {
  position: relative;
  padding: 6px 12px;
  background: $bg-ui-selected;
  color: $primary-color;
  font-size: 13px;
  font-weight: 500;
  border-radius: 16px;
  text-align: center;
  white-space: nowrap;
  transition: all 0.1s ease;

  &:hover {
    transform: translateY(-1px);
  }

  &[data-tooltip]:not([data-tooltip=""]) {
    cursor: default;

    &::before {
      content: attr(data-tooltip);
      position: absolute;
      bottom: calc(100% + 8px);
      left: 50%;
      transform: translateX(-50%) translateY(-4px);
      padding: 6px 10px;
      background: #424242;
      color: #fff;
      font-size: 12px;
      font-weight: 500;
      border-radius: 6px;
      white-space: normal;
      max-width: 300px;
      min-width: 120px;
      text-align: center;
      pointer-events: none;
      opacity: 0;
      transition: opacity 0.1s ease, transform 0.1s ease;
      z-index: 1000;
    }

    &::after {
      content: '';
      position: absolute;
      bottom: calc(100% - 2px);
      left: 50%;
      transform: translateX(-50%) translateY(-4px);
      border: 6px solid transparent;
      border-top-color: #424242;
      pointer-events: none;
      opacity: 0;
      transition: opacity 0.1s ease, transform 0.1s ease;
      z-index: 1000;
    }

    &:hover::before,
    &:hover::after {
      opacity: 1;
      transform: translateX(-50%) translateY(0);
    }
  }
}
.onboarding-extension-guide {
  width: 100%;
  max-width: 700px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.onboarding-extension-step {
  display: flex;
  gap: 20px;
  padding: 24px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  align-items: flex-start;
}

.onboarding-step-number {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: $primary-color;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-weight: 700;
  flex-shrink: 0;
}

.onboarding-step-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.onboarding-step-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #111827;
}

.onboarding-step-description {
  margin: 0;
  font-size: 14px;
  color: #6b7280;
  line-height: 1.5;
}

.onboarding-btn-extension,
.onboarding-btn-connect {
  padding: 12px 24px;
  background-color: $primary-color;
  color: #fff;
  border: none;
  border-radius: 40px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
  text-align: center;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  align-self: flex-start;

  &:hover {
    background-color: darken($primary-color, 8%);
  }

  .onboarding-btn-icon {
    width: 16px;
    height: 16px;
    transform: translateY(-1px);
  }

  .onboarding-btn-icon2 {
    width: 20px;
    height: 20px;
    transform: translateY(-1px);
  }
}

.onboarding-btn-connect {
  background-color: $primary-color;
  
  &:hover {
    background-color: darken($primary-color, 8%);
  }
}

/* GIF tutorial toggle button */
.onboarding-gif-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  padding: 0;
  font-size: 13px;
  color: $primary-color;
  cursor: pointer;
  font-weight: 500;
  text-decoration: underline;
  text-underline-offset: 2px;
  align-self: flex-start;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 0.75;
  }

  svg {
    flex-shrink: 0;
  }
}

/* GIF tutorial container */
.onboarding-gif-container {
  position: relative;
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  background: #f3f4f6;
  cursor: zoom-in;

  .onboarding-gif {
    display: block;
    width: 100%;
    height: auto;
    max-height: 260px;
    object-fit: contain;
  }

  .onboarding-gif-expand-hint {
    position: absolute;
    bottom: 8px;
    right: 8px;
    background: rgba(0, 0, 0, 0.45);
    border-radius: 6px;
    padding: 5px;
    display: flex;
    align-items: center;
    color: #fff;
    opacity: 0;
    transition: opacity 0.15s ease;
    pointer-events: none;
  }

  &:hover .onboarding-gif-expand-hint {
    opacity: 1;
  }
}

/* GIF lightbox overlay */
.onboarding-gif-lightbox {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.82);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: zoom-out;

  &-close {
    position: absolute;
    top: 16px;
    right: 16px;
    background: rgba(0, 0, 0, 0.5);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 50%;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: #fff;
    transition: background 0.15s ease;

    &:hover {
      background: rgba(0, 0, 0, 0.72);
    }
  }

  &-img {
    max-width: 90vw;
    max-height: 85vh;
    border-radius: 8px;
    object-fit: contain;
    cursor: default;
    box-shadow: 0 24px 80px rgba(0, 0, 0, 0.5);
  }
}

.lightbox-fade-enter-active,
.lightbox-fade-leave-active {
  transition: opacity 0.2s ease;
}

.lightbox-fade-enter,
.lightbox-fade-leave-to {
  opacity: 0;
}

.onboarding-video-container {
  position: relative;
  width: 100%;
  padding-bottom: 56.25%; /* 16:9 aspect ratio */
  background: #f3f4f6;
  border-radius: 8px;
  overflow: hidden;
}

.onboarding-video {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

/* Footer section - réutilisé de StepConnectNatives */
.onboarding-footer-container {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: 40px;
}

.onboarding-footer {
  display: flex;
  align-items: center;
  gap: 12px;

  &.centered {
    justify-content: center;
  }

  .onboarding-btn-secondary,
  .onboarding-btn-primary {
    padding: 12px 24px;
    font-size: 15px;
    font-weight: 600;
    border-radius: 20px;
    cursor: pointer;
    transition: all 0.2s ease;
    border: none;
  }

  .onboarding-btn-secondary {
    background: transparent;
    color: #6b7280;

    &:hover {
      color: #111827;
    }
  }

  .onboarding-btn-primary {
    background: $bg-ui-selected;
    color: $primary-color;
    display: flex;
    align-items: center;
    gap: 12px;

    &:hover:not(:disabled) {
      background: $primary-color;
      color: #ffffff;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
}

/* Extension hint with icon */
.onboarding-extension-hint {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  background: #ffffff;
  border: 1px dashed #d1d5db;
  border-radius: 8px;
  font-size: 13px;
  color: #6b7280;
  font-style: italic;
}

/* Success container after extension connection */
.onboarding-success-container {
  width: 100%;
  max-width: 700px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 24px;
  padding: 40px;
  background: #ffffff;
  // border: 2px solid #10B981;
  border: 1px dashed #6b1f4d;
  border-radius: 16px;
}

.onboarding-success-icon {
  margin-bottom: 8px;
}

.onboarding-success-title {
  font-size: 32px;
  font-weight: 700;
  color: #111827;
  margin: 0;
}

.onboarding-success-subtitle {
  font-size: 16px;
  color: #6b7280;
  margin: 0;
  max-width: 50ch;
}

.onboarding-success-pages {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 8px;
}

.onboarding-success-page-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  text-align: left;
  transition: all 0.2s;

  &:hover {
    background: #f3f4f6;
    border-color: #d1d5db;
  }
}

.onboarding-success-page-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;

  &.avatar-placeholder {
    background-color: $primary-color;
    color: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    font-size: 20px;
  }
}

.onboarding-success-page-info {
  flex: 1;
  min-width: 0;
}

.onboarding-success-page-name {
  font-size: 16px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 4px 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.onboarding-success-page-username {
  font-size: 14px;
  color: #6b7280;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.onboarding-success-page-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: #d1fae5;
  color: #065f46;
  border-radius: 16px;
  font-size: 13px;
  font-weight: 500;
  flex-shrink: 0;

  svg {
    width: 16px;
    height: 16px;
  }
}

.onboarding-success-footer {
  font-size: 15px;
  color: #111827;
  font-weight: 500;
  margin-top: 8px;
}

/* Custom styles for modalPopup in this component only */
.onboarding-warning-modal ::v-deep .popup {
  width: 500px;

  p {
    margin-bottom: 14px;
  }

  ul {
    text-align: left;

    li {
      padding: 4px 0;
      padding-left: 28px;
      position: relative;
      color: #374151;
      font-size: 14px;
      line-height: 1.5;

      &::before {
        content: '✓';
        position: absolute;
        left: 0;
        top: 50%;
        transform: translateY(-50%);
        color: $primary-color;
        font-weight: bold;
        font-size: 16px;
      }
    }
  }
}

@media (max-width: 800px) {
  .onboarding-content {
    padding: 32px 24px;

    .onboarding-header-title {
      font-size: 24px;
    }
  }

  .onboarding-extension-guide {
    max-width: 100%;
  }

  .onboarding-footer-container {
    .onboarding-footer {
      .onboarding-step-indicator {
        display: none;
      }
    }
  }
}
</style>
